import { apiFetch, buildUrl, ApiError } from "@/lib/api";
import type { UserDto } from "@/types/api";

export async function login(email: string, password: string, totpCode?: string): Promise<UserDto> {
  return apiFetch<UserDto>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password, totpCode }),
  });
}

export async function requestMagicLink(email: string): Promise<void> {
  await apiFetch<null>("/auth/magic-link", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function verifyMagicLink(token: string): Promise<UserDto | null> {
  const url = buildUrl("/auth/verify", { token });
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-Tenant-Slug": process.env.NEXT_PUBLIC_TENANT_SLUG || "demo",
    },
  });
  const envelope = await response.json();
  if (!response.ok || !envelope.success) {
    throw new ApiError(
      envelope.message || "Verification failed",
      response.status,
      envelope.errors,
    );
  }
  return envelope.data;
}

export async function logout(): Promise<void> {
  await apiFetch<null>("/auth/logout", { method: "POST" });
}

export async function getCurrentUser(): Promise<UserDto | null> {
  try {
    return await apiFetch<UserDto>("/auth/me");
  } catch {
    return null;
  }
}

export async function updatePassword(data: {
  currentPassword?: string;
  newPassword: string;
  totpCode?: string;
}): Promise<UserDto> {
  return apiFetch<UserDto>("/auth/password", {
    method: "POST",
    body: JSON.stringify({
      currentPassword: data.currentPassword || undefined,
      newPassword: data.newPassword,
      totpCode: data.totpCode || undefined,
    }),
  });
}

export function isEditorOrAdmin(role: string | undefined): boolean {
  return role === "admin" || role === "editor" || role === "super_admin";
}

export function disableTotp(currentPassword: string, code: string): Promise<UserDto> {
  return apiFetch<UserDto>("/auth/totp/disable", {
    method: "POST", body: JSON.stringify({ currentPassword: currentPassword || undefined, code }),
  });
}

export function isAdmin(role: string | undefined): boolean {
  return role === "admin" || role === "super_admin";
}
